import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Tag,
    Space,
    message,
    Popconfirm,
    Row,
    Col,
    Statistic,
    Typography,
    Divider,
    Collapse,
} from 'antd';
import {
    SearchOutlined,
    DeleteOutlined,
    ReloadOutlined,
    EyeOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    BugOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const SystemLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [stats, setStats] = useState({});
    const [filters, setFilters] = useState({});

    useEffect(() => {
        fetchLogs();
        fetchStats();
    }, [filters]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetSystemLogs(filters);
            setLogs(response.data.data);
        } catch (error) {
            message.error('Failed to fetch system logs');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.adminGetSystemLogStats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const handleView = (log) => {
        setSelectedLog(log);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteSystemLog(id);
            message.success('Log entry deleted successfully');
            fetchLogs();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to delete log entry');
        }
    };

    const handleCleanup = async () => {
        try {
            const response = await api.adminCleanupSystemLogs(30);
            message.success(response.message);
            fetchLogs();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to cleanup logs');
        }
    };

    const getLevelColor = (level) => {
        switch (level) {
            case 'critical': return 'red';
            case 'error': return 'red';
            case 'warning': return 'orange';
            case 'info': return 'blue';
            case 'debug': return 'green';
            default: return 'default';
        }
    };

    const getLevelIcon = (level) => {
        switch (level) {
            case 'critical': return <CloseCircleOutlined />;
            case 'error': return <ExclamationCircleOutlined />;
            case 'warning': return <WarningOutlined />;
            case 'info': return <InfoCircleOutlined />;
            case 'debug': return <BugOutlined />;
            default: return null;
        }
    };

    const getChannelColor = (channel) => {
        switch (channel) {
            case 'api': return 'blue';
            case 'database': return 'green';
            case 'auth': return 'purple';
            case 'payment': return 'orange';
            case 'email': return 'cyan';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            width: 100,
            render: (level) => (
                <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
                    {level.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Channel',
            dataIndex: 'channel',
            key: 'channel',
            width: 100,
            render: (channel) => (
                <Tag color={getChannelColor(channel)}>
                    {channel.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            ellipsis: true,
            render: (text) => (
                <Text ellipsis={{ tooltip: text }}>
                    {text}
                </Text>
            ),
        },
        {
            title: 'User',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 100,
            render: (userId) => userId || '-',
        },
        {
            title: 'IP',
            dataIndex: 'ip_address',
            key: 'ip_address',
            width: 120,
            render: (ip) => ip || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status_code',
            key: 'status_code',
            width: 80,
            render: (code) => code ? (
                <Tag color={code >= 400 ? 'red' : code >= 300 ? 'orange' : 'green'}>
                    {code}
                </Tag>
            ) : '-',
        },
        {
            title: 'Time',
            dataIndex: 'execution_time',
            key: 'execution_time',
            width: 100,
            render: (time) => time ? `${time}ms` : '-',
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm:ss'),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                    >
                        View
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this log entry?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>System Logs</Title>
                <Text type="secondary">
                    Monitor and manage system logs and errors
                </Text>
            </div>

            {/* Statistics */}
            <Row gutter={16} className="mb-6">
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Total Logs"
                            value={stats.total || 0}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Errors Today"
                            value={stats.errors_today || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="By Level"
                            value={Object.keys(stats.by_level || {}).length}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="By Channel"
                            value={Object.keys(stats.by_channel || {}).length}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Critical"
                            value={stats.by_level?.critical || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Errors"
                            value={stats.by_level?.error || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <Title level={4} className="!mb-0">System Logs</Title>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchLogs}
                        >
                            Refresh
                        </Button>
                        <Popconfirm
                            title="Are you sure you want to cleanup logs older than 30 days?"
                            onConfirm={handleCleanup}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Cleanup Old Logs
                            </Button>
                        </Popconfirm>
                    </Space>
                </div>

                {/* Filters */}
                <div className="mb-4">
                    <Row gutter={16}>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by level"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, level: value })}
                            >
                                <Option value="debug">Debug</Option>
                                <Option value="info">Info</Option>
                                <Option value="warning">Warning</Option>
                                <Option value="error">Error</Option>
                                <Option value="critical">Critical</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by channel"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, channel: value })}
                            >
                                <Option value="default">Default</Option>
                                <Option value="api">API</Option>
                                <Option value="database">Database</Option>
                                <Option value="auth">Auth</Option>
                                <Option value="payment">Payment</Option>
                                <Option value="email">Email</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Input
                                placeholder="Search message"
                                allowClear
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            />
                        </Col>
                        <Col span={6}>
                            <RangePicker
                                style={{ width: '100%' }}
                                onChange={(dates) => {
                                    if (dates) {
                                        setFilters({
                                            ...filters,
                                            date_from: dates[0]?.format('YYYY-MM-DD'),
                                            date_to: dates[1]?.format('YYYY-MM-DD'),
                                        });
                                    } else {
                                        setFilters({
                                            ...filters,
                                            date_from: undefined,
                                            date_to: undefined,
                                        });
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={logs}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} log entries`,
                    }}
                />
            </Card>

            <Modal
                title="Log Details"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedLog && (
                    <div>
                        <Row gutter={16} className="mb-4">
                            <Col span={12}>
                                <Text strong>Level: </Text>
                                <Tag color={getLevelColor(selectedLog.level)} icon={getLevelIcon(selectedLog.level)}>
                                    {selectedLog.level.toUpperCase()}
                                </Tag>
                            </Col>
                            <Col span={12}>
                                <Text strong>Channel: </Text>
                                <Tag color={getChannelColor(selectedLog.channel)}>
                                    {selectedLog.channel.toUpperCase()}
                                </Tag>
                            </Col>
                        </Row>

                        <div className="mb-4">
                            <Text strong>Message:</Text>
                            <div className="mt-2 p-3 bg-gray-50 rounded">
                                <Text>{selectedLog.message}</Text>
                            </div>
                        </div>

                        <div className="mb-4">
                            <Text strong>Created:</Text>
                            <Text className="ml-2">
                                {dayjs(selectedLog.created_at).format('MMMM DD, YYYY HH:mm:ss')}
                            </Text>
                        </div>

                        {selectedLog.user_id && (
                            <div className="mb-4">
                                <Text strong>User ID:</Text>
                                <Text className="ml-2">{selectedLog.user_id}</Text>
                            </div>
                        )}

                        {selectedLog.ip_address && (
                            <div className="mb-4">
                                <Text strong>IP Address:</Text>
                                <Text className="ml-2">{selectedLog.ip_address}</Text>
                            </div>
                        )}

                        {selectedLog.url && (
                            <div className="mb-4">
                                <Text strong>URL:</Text>
                                <Text className="ml-2">{selectedLog.url}</Text>
                            </div>
                        )}

                        {selectedLog.method && (
                            <div className="mb-4">
                                <Text strong>Method:</Text>
                                <Text className="ml-2">{selectedLog.method}</Text>
                            </div>
                        )}

                        {selectedLog.status_code && (
                            <div className="mb-4">
                                <Text strong>Status Code:</Text>
                                <Tag color={selectedLog.status_code >= 400 ? 'red' : selectedLog.status_code >= 300 ? 'orange' : 'green'} className="ml-2">
                                    {selectedLog.status_code}
                                </Tag>
                            </div>
                        )}

                        {selectedLog.execution_time && (
                            <div className="mb-4">
                                <Text strong>Execution Time:</Text>
                                <Text className="ml-2">{selectedLog.execution_time}ms</Text>
                            </div>
                        )}

                        {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                            <div className="mb-4">
                                <Text strong>Context:</Text>
                                <div className="mt-2 p-3 bg-gray-50 rounded">
                                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0 }}>
                                        {JSON.stringify(selectedLog.context, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}

                        {selectedLog.extra && Object.keys(selectedLog.extra).length > 0 && (
                            <div className="mb-4">
                                <Text strong>Extra Data:</Text>
                                <div className="mt-2 p-3 bg-gray-50 rounded">
                                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0 }}>
                                        {JSON.stringify(selectedLog.extra, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}

                        {selectedLog.stack_trace && (
                            <div className="mb-4">
                                <Text strong>Stack Trace:</Text>
                                <div className="mt-2 p-3 bg-gray-50 rounded">
                                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                                        {selectedLog.stack_trace}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SystemLogs;
